import React, { useState, useEffect } from 'react';
import { ListRowSkeletonStack } from '../../../components/loading/skeletons';

/**
 * Parse patch notes content into entries (newest first in file = after first ---).
 * Format: "- **Label** (scope): description — `hash` (date)" with optional body lines (2-space indent).
 */
function parsePatchNotes(text) {
  if (!text || !text.trim()) return [];
  const parts = text.split(/\n---\n\n/);
  const entries = [];
  for (let i = 0; i < parts.length; i++) {
    const block = parts[i].trim();
    if (!block || block.startsWith('# Patch notes') || block.startsWith('Entries are')) continue;
    const lines = block.split('\n');
    let first = lines[0];
    if (!first || !first.startsWith('- ')) continue;
    first = first.slice(2).replace(/-{2,}\s*$/, '').trim();
    const main = first;
    const metaMatch = main.match(/\s+[—\-]\s+`[^`]+`\s+\((\d{4}-\d{2}-\d{2})\)\s*$/);
    const date = metaMatch ? metaMatch[1] : '';
    const mainText = metaMatch ? main.slice(0, main.search(/\s+[—\-]\s+`[^`]+`\s+\(\d{4}-\d{2}-\d{2}\)\s*$/)).trim() : main;
    const boldMatch = mainText.match(/^\*\*([^*]+)\*\*(?:\s*\(([^)]*)\))?:?\s*(.*)$/);
    const label = boldMatch ? boldMatch[1] : null;
    const scope = boldMatch ? boldMatch[2] || null : null;
    const description = boldMatch ? (boldMatch[3] || '').trim() : mainText;
    const bodyLines = lines.slice(1).filter((l) => l.startsWith('  ')).map((l) => l.replace(/^  /, ''));
    const body = bodyLines.length ? bodyLines.join('\n').trim() : null;
    entries.push({ label, scope, description: description || mainText, date, body });
  }
  return entries;
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return '';
  try {
    const [y, m, d] = isoDate.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

function getYearMonthKey(isoDate) {
  if (!isoDate) return 'Unknown';
  const match = isoDate.match(/^(\d{4})-(\d{2})-\d{2}$/);
  return match ? `${match[1]}-${match[2]}` : 'Unknown';
}

function getYearKey(isoDate) {
  if (!isoDate) return 'Unknown';
  const match = isoDate.match(/^(\d{4})-\d{2}-\d{2}$/);
  return match ? match[1] : 'Unknown';
}

function formatMonthLabel(yearMonthKey) {
  if (!yearMonthKey || yearMonthKey === 'Unknown') return 'Unknown month';
  try {
    const [y, m] = yearMonthKey.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long' });
  } catch {
    return yearMonthKey;
  }
}

function groupEntriesByDate(entries) {
  const grouped = new Map();
  entries.forEach((entry) => {
    const key = entry.date || 'Unknown';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(entry);
  });
  return [...grouped.entries()].sort(([a], [b]) => b.localeCompare(a));
}

function isFixBullet(text) {
  return /^fix/i.test((text || '').trim());
}

const BULLET_SEP = /\s*\/\/\s*|\s+\/\s+/;

/** Flatten all entries into a single list of bullet strings (each entry's description split by " / "). */
function flattenBullets(entries) {
  const result = [];
  for (const entry of entries) {
    const bullets = (entry.description || '').split(BULLET_SEP).map((s) => s.trim()).filter(Boolean);
    result.push(...bullets);
  }
  return result;
}

function splitEntriesBySection(entries) {
  const updateEntries = [];
  const fixEntries = [];
  for (const entry of entries) {
    const bullets = (entry.description || '').split(BULLET_SEP).map((s) => s.trim()).filter(Boolean);
    const fixBullets = bullets.filter((b) => isFixBullet(b));
    const updateBullets = bullets.filter((b) => !isFixBullet(b));
    if (entry.label === 'Fixed' && bullets.length === 0 && (entry.description || '').trim()) {
      fixEntries.push(entry);
      continue;
    }
    if (bullets.length === 0) {
      updateEntries.push(entry);
      continue;
    }
    if (updateBullets.length > 0) {
      updateEntries.push({ ...entry, description: updateBullets.join(' // ') });
    }
    if (fixBullets.length > 0) {
      fixEntries.push({ ...entry, description: fixBullets.join(' // ') });
    }
  }
  return { updateEntries, fixEntries };
}

const PatchNotesPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    fetch(`${base}/versions/versions-list.json`)
      .then((res) => {
        if (res.status === 404) {
          setEntries([]);
          setError(null);
          setLoading(false);
          return null;
        }
        if (!res.ok) throw new Error('Could not load version list');
        return res.text();
      })
      .then((text) => {
        if (text === null) return;
        let data;
        try {
          data = text && text.trim().startsWith('{') ? JSON.parse(text) : null;
        } catch {
          data = null;
        }
        const files = data?.versions;
        if (!files || !Array.isArray(files) || files.length === 0) {
          setEntries([]);
          setError(null);
          setLoading(false);
          return;
        }
        const sortedFiles = [...files].sort((a, b) => {
          const numA = parseInt(a.match(/version-(\d+)\.md/)?.[1] || '0', 10);
          const numB = parseInt(b.match(/version-(\d+)\.md/)?.[1] || '0', 10);
          return numB - numA;
        });
        const toFetch = sortedFiles;
        return Promise.all(
          toFetch.map((f) =>
            fetch(`${base}/versions/${f}`).then((r) => (r.ok ? r.text() : ''))
          )
        ).then((texts) => {
          try {
            const combined = texts.filter(Boolean).join('\n---\n\n');
            setEntries(parsePatchNotes(combined));
            setError(null);
          } catch (e) {
            setError(e?.message || 'Failed to parse patch notes');
            setEntries([]);
          }
        });
      })
      .catch((err) => {
        setError(err.message);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (entries.length === 0) return;
    const months = [...new Set(entries.map((e) => getYearMonthKey(e.date)))].sort((a, b) => b.localeCompare(a));
    setExpandedMonths((prev) => (prev.size === 0 ? new Set([months[0]]) : prev));
  }, [entries.length]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="admin-page" style={{ maxWidth: 960 }}>
        <ListRowSkeletonStack rows={8} variant="compact" label="Loading patch notes" />
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ maxWidth: 960 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 className="admin-heading">
          Patch notes
        </h1>
        <p className="admin-muted" style={{ marginTop: 6, fontSize: 13 }}>
          Recent changes and improvements to the app.
        </p>
      </div>

      {error ? <p className="admin-alert">{error}</p> : null}

      {!error && entries.length === 0 && (
        <p className="admin-muted" style={{ padding: '32px 0', textAlign: 'center' }}>
          No updates yet. Check back later.
        </p>
      )}

      {!error && entries.length > 0 && (() => {
        const { updateEntries: deployedUpdates, fixEntries: deployedFixes } = splitEntriesBySection(entries);
        const updatesByMonth = new Map();
        const fixesByMonth = new Map();
        deployedUpdates.forEach((e) => {
          const key = getYearMonthKey(e.date);
          if (!updatesByMonth.has(key)) updatesByMonth.set(key, []);
          updatesByMonth.get(key).push(e);
        });
        deployedFixes.forEach((e) => {
          const key = getYearMonthKey(e.date);
          if (!fixesByMonth.has(key)) fixesByMonth.set(key, []);
          fixesByMonth.get(key).push(e);
        });
        const allMonths = [...new Set([...updatesByMonth.keys(), ...fixesByMonth.keys()])].sort((a, b) => b.localeCompare(a));
        const monthsByYear = new Map();
        allMonths.forEach((monthKey) => {
          const yearKey = monthKey === 'Unknown' ? 'Unknown' : getYearKey(`${monthKey}-01`);
          if (!monthsByYear.has(yearKey)) monthsByYear.set(yearKey, []);
          monthsByYear.get(yearKey).push(monthKey);
        });
        const allYears = [...monthsByYear.keys()].sort((a, b) => b.localeCompare(a));

        return (
          <>
            {allYears.map((year) => (
              <div key={year} style={{ marginBottom: 32 }}>
                <h2 className="admin-heading-sm" style={{ marginBottom: 16 }}>
                  {year === 'Unknown' ? 'Unknown year' : year}
                </h2>
                {(monthsByYear.get(year) || []).map((monthKey) => {
                  const monthLabel = formatMonthLabel(monthKey);
                  const updateEntriesForMonth = updatesByMonth.get(monthKey) || [];
                  const fixEntriesForMonth = fixesByMonth.get(monthKey) || [];
                  const hasAny = updateEntriesForMonth.length > 0 || fixEntriesForMonth.length > 0;
                  if (!hasAny) return null;
                  const isExpanded = expandedMonths.has(monthKey);
                  return (
                    <div key={monthKey} className="admin-card" style={{ marginBottom: 16 }}>
                      <button
                        type="button"
                        onClick={() => toggleMonth(monthKey)}
                        aria-expanded={isExpanded}
                        className="admin-settings-section-head"
                        style={{ padding: 0 }}
                      >
                        <strong className="admin-muted" style={{ fontWeight: 600 }}>
                          {monthLabel}
                        </strong>
                        <span aria-hidden="true">{isExpanded ? '▴' : '▾'}</span>
                      </button>
                      {isExpanded ? (
                        <div style={{ paddingTop: 16 }}>
                          {(() => {
                            const updatesByDate = groupEntriesByDate(updateEntriesForMonth);
                            const fixesByDate = groupEntriesByDate(fixEntriesForMonth);
                            const allDates = [...new Set([
                              ...updatesByDate.map(([date]) => date),
                              ...fixesByDate.map(([date]) => date),
                            ])].sort((a, b) => b.localeCompare(a));
                            const updatesByDateMap = new Map(updatesByDate);
                            const fixesByDateMap = new Map(fixesByDate);
                            return (
                              <>
                                {allDates.map((date, index) => {
                                  const updateBullets = flattenBullets(updatesByDateMap.get(date) || []);
                                  const fixBullets = flattenBullets(fixesByDateMap.get(date) || []);
                                  const hasDateContent = updateBullets.length > 0 || fixBullets.length > 0;
                                  if (!hasDateContent) return null;
                                  return (
                                    <div key={date} style={{ marginTop: index === 0 ? 0 : 20 }}>
                                      <p style={{ margin: '0 0 8px', fontSize: '0.875rem', fontWeight: 600 }}>
                                        {formatDisplayDate(date) || date}
                                      </p>
                                      {updateBullets.length > 0 && (
                                        <>
                                          <p className="admin-kicker" style={{ color: 'var(--admin-primary)', marginBottom: 8 }}>
                                            Deployed updates
                                          </p>
                                          <BulletList items={updateBullets} />
                                        </>
                                      )}
                                      {fixBullets.length > 0 && (
                                        <div style={{ marginTop: updateBullets.length > 0 ? 16 : 0 }}>
                                          <p className="admin-kicker" style={{ color: 'var(--admin-primary)', marginBottom: 8 }}>
                                            Deployed fixes
                                          </p>
                                          <BulletList items={fixBullets} />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        );
      })()}
    </div>
  );
};

function BulletList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: '1.5em', fontSize: '1rem', lineHeight: 1.25 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default PatchNotesPage;
